import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GitBranch, Plus } from 'lucide-react';
import {
  CASCADE_MAX_NODES,
  FIBRE_DB_PER_KM,
  MAX_TOPOLOGY_DEPTH,
  SPLITTER_CAPACITIES,
  SPLITTER_KIND_LABELS,
  SPLITTER_KIND_RANK,
  cascadeLossDb,
  fibreLossDb,
  formatDistance,
  planCascade,
  upstreamCapacities,
  type CascadeChainResult,
  type CascadeLevelSpec,
  type CascadePlanAnchor,
  type Olt,
  type Page,
  type PonPort,
  type SplitterKind,
  type Topology,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { useTasks } from '../lib/tasks';
import { Button, ErrorNotice, Field, Input } from '../components/ui';
import { WizardDialog } from '../components/WizardDialog';
import { ZoneSelect } from '../components/topology-select';

/**
 * The cascade builder — a whole splitter cascade composed level by level and
 * created in one reviewed run.
 *
 * Anchor first: an OLT PON port (the chain starts with a main splitter) or an
 * existing splitter with free outputs. Then the chain, one level at a time —
 * kind, size, fan-out per parent, and a name pattern — with a live loss
 * readout per level from the signal diagram's own table (`planCascade` in
 * shared computes both). The review step lists every splitter with its
 * parent and port before anything is written; apply CLOSES the wizard and
 * hands the bulk write to the background task tray, which reports the outcome
 * once the run settles — no spinner held open, no result step to dismiss.
 */

interface LevelDraft {
  kind: SplitterKind;
  capacity: number;
  /** Kept as typed, so an emptied field does not snap to a number under the cursor. */
  countPerParent: string;
  namePattern: string;
  /** Planned fibre from the level above, metres, as typed; empty = not known, adds nothing (2026-09-16). */
  fibreLengthM: string;
}

const DEFAULT_PATTERNS: Record<SplitterKind, string> = {
  odf: 'ODF-{n}',
  enclosure: 'ENC-{n}',
  fat: 'FAT-{n}',
};

function nextKind(previous: SplitterKind | null): SplitterKind {
  if (!previous) return 'odf';
  if (previous === 'odf') return 'enclosure';
  return 'fat';
}

const ALL_KINDS = Object.keys(SPLITTER_KIND_LABELS) as SplitterKind[];

/** The pill row: anchor, chain, then the review the write is gated behind. */
const CASCADE_STEPS = [
  { key: 'anchor', title: 'The anchor' },
  { key: 'chain', title: 'The chain' },
  { key: 'review', title: 'Review' },
];

interface Occupancy {
  capacity: number;
  children: number;
  onus: number;
  free: number;
}

export function CascadeWizard({
  topology,
  olts,
  onClose,
  onDone,
}: {
  topology: Topology;
  olts: Olt[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const tasks = useTasks();
  const [step, setStep] = useState(0);
  const [anchorType, setAnchorType] = useState<'port' | 'splitter'>('port');
  const [oltId, setOltId] = useState('');
  const [ponPort, setPonPort] = useState('');
  const [anchorSplitterId, setAnchorSplitterId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [levels, setLevels] = useState<LevelDraft[]>([]);

  const splitters = topology.splitters;
  const anchorSplitter = useMemo(
    () => splitters.find((s) => s.id === anchorSplitterId) ?? null,
    [splitters, anchorSplitterId],
  );

  const ports = useQuery({
    queryKey: ['olt-pon-ports', oltId],
    queryFn: () => api.get<Page<PonPort>>(`/olts/${encodeURIComponent(oltId)}/pon-ports`),
    enabled: anchorType === 'port' && oltId !== '',
  });

  // Authoritative fill for the picked anchor — the same counter the apply's
  // guardrail reads, so the free count shown is the one that will be enforced.
  const occupancy = useQuery({
    queryKey: ['splitter-occupancy', anchorSplitterId],
    queryFn: () =>
      api.get<Occupancy>(`/splitters/${encodeURIComponent(anchorSplitterId)}/occupancy`),
    enabled: anchorType === 'splitter' && anchorSplitterId !== '',
  });

  const anchor: CascadePlanAnchor | null = useMemo(() => {
    if (anchorType === 'port') {
      if (!oltId || !ponPort) return null;
      return {
        type: 'port',
        oltId,
        ponPort,
        oltName: olts.find((o) => o.id === oltId)?.name,
      };
    }
    if (!anchorSplitter) return null;
    const chain = upstreamCapacities(anchorSplitter.id, splitters);
    const estimated =
      splitters.filter((s) => s.parentId === anchorSplitter.id).length +
      (anchorSplitter.deviceCount ?? 0);
    return {
      type: 'splitter',
      id: anchorSplitter.id,
      name: anchorSplitter.name,
      kind: anchorSplitter.kind,
      capacity: anchorSplitter.capacity,
      depth: chain.length - 1,
      occupied: occupancy.data ? occupancy.data.children + occupancy.data.onus : estimated,
      lossDb: cascadeLossDb(chain),
      // The fibre measured down to the anchor along the routes drawn on the
      // map (2026-09-16) — null when a hop above it is not drawn, which adds
      // nothing and leaves the readout the splitters' alone.
      feedLengthM: anchorSplitter.feedLengthM ?? null,
    };
  }, [anchorType, oltId, ponPort, olts, anchorSplitter, splitters, occupancy.data]);

  const levelSpecs: CascadeLevelSpec[] = useMemo(
    () =>
      levels.map((level, i) => ({
        kind: level.kind,
        capacity: level.capacity,
        // One main splitter per port — the input is locked to 1 in that case,
        // so the plan must not read a fan-out typed under an earlier anchor.
        countPerParent:
          i === 0 && anchorType === 'port' ? 1 : Number(level.countPerParent),
        namePattern: level.namePattern,
        fibreLengthM: level.fibreLengthM.trim() === '' ? null : Number(level.fibreLengthM),
      })),
    [levels, anchorType],
  );
  const plan = useMemo(
    () =>
      anchor
        ? planCascade(anchor, levelSpecs)
        : { nodes: [], levels: [], total: 0, problems: [] },
    [anchor, levelSpecs],
  );

  // How many levels the model allows below this anchor (5 tiers total).
  const baseDepth = anchor?.type === 'splitter' ? anchor.depth + 1 : 0;
  const maxLevels = MAX_TOPOLOGY_DEPTH + 1 - baseDepth;

  function seedLevels() {
    if (levels.length > 0) return;
    const anchorKind = anchor?.type === 'splitter' ? anchor.kind : null;
    const kind = nextKind(anchorKind);
    const namePattern =
      anchor?.type === 'port' ? `ODF ${anchor.ponPort}` : DEFAULT_PATTERNS[kind];
    setLevels([{ kind, capacity: 8, countPerParent: '1', namePattern, fibreLengthM: '' }]);
  }

  function setLevel(index: number, patch: Partial<LevelDraft>) {
    setLevels((all) =>
      all.map((level, i) => {
        if (i !== index) return level;
        const next = { ...level, ...patch };
        // A pattern still wearing its default follows a kind change.
        if (patch.kind && level.namePattern === DEFAULT_PATTERNS[level.kind]) {
          next.namePattern = DEFAULT_PATTERNS[patch.kind];
        }
        return next;
      }),
    );
  }

  function addLevel() {
    setLevels((all) => {
      const previous = all[all.length - 1];
      const kind = nextKind(previous?.kind ?? null);
      return [
        ...all,
        { kind, capacity: 8, countPerParent: '1', namePattern: DEFAULT_PATTERNS[kind], fibreLengthM: '' },
      ];
    });
  }

  /**
   * Close on submit and hand the bulk org-write to the background task tray —
   * the wizard does not sit open on a spinner and then a result the operator
   * must dismiss. The body is captured before onClose() because the component
   * is gone by the time the task settles; the tray's done message is the
   * report the removed step-3 used to render.
   */
  function startBuild() {
    const body = {
      zoneId: zoneId || null,
      anchor:
        anchorType === 'port' ? { oltId, ponPort } : { splitterId: anchorSplitterId },
      nodes: plan.nodes.map((node) => ({
        name: node.name,
        kind: node.kind,
        capacity: node.capacity,
        parentIndex: node.parentIndex,
      })),
    };
    const total = plan.total;
    onClose();
    tasks.start<CascadeChainResult>({
      label: `Build cascade — ${total} splitter(s)`,
      run: () => api.post<CascadeChainResult>('/splitters/cascade', body),
      invalidate: [['topology']],
      done: (r) => {
        const message =
          `Cascade: ${r.created} splitter${r.created === 1 ? '' : 's'} created` +
          `${r.failed ? `, ${r.failed} failed` : ''}` +
          `${r.skipped ? `, ${r.skipped} skipped` : ''}.`;
        onDone(message);
        return message;
      },
    });
  }

  const canNext = step === 0 ? anchor !== null : step === 1 ? levels.length > 0 : true;

  const anchorLine =
    anchor?.type === 'port'
      ? `${anchor.oltName ?? 'OLT'} · ${anchor.ponPort}`
      : anchor
        ? `${anchor.name} — ${SPLITTER_KIND_LABELS[anchor.kind]} 1×${anchor.capacity}, ` +
          `${Math.max(0, anchor.capacity - anchor.occupied)} of ${anchor.capacity} output(s) free, ` +
          `−${anchor.lossDb.toFixed(1)} dB of splitters already at the anchor` +
          (anchor.feedLengthM !== null && anchor.feedLengthM !== undefined
            ? ` · feed: ${formatDistance(anchor.feedLengthM)} · +${fibreLossDb(anchor.feedLengthM).toFixed(1)} dB`
            : '')
        : '';

  return (
    <WizardDialog
      title={
        <span className="flex items-center gap-2">
          <GitBranch className="size-4 text-muted" />
          Build a cascade
        </span>
      }
      subtitle="Compose the chain level by level, review every splitter it would create, then build it. Nothing is written before the review."
      onClose={onClose}
      maxWidth="3xl"
      steps={CASCADE_STEPS}
      currentStep={step}
      bodyClassName="max-h-[60vh] space-y-4 overflow-y-auto p-5"
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
            Back
          </Button>
          {step < 2 ? (
            <Button
              variant="primary"
              disabled={!canNext}
              onClick={() => {
                if (step === 0) seedLevels();
                setStep((s) => s + 1);
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={plan.problems.length > 0 || plan.nodes.length === 0}
              onClick={startBuild}
            >
              Create
            </Button>
          )}
        </div>
      }
    >
      <>
        {step === 0 && (
          <>
            <Field
              label="The cascade hangs on"
              hint="A main splitter starts at a PON port; a deeper chain grows from an existing splitter's free outputs"
            >
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={anchorType === 'port' ? 'primary' : 'secondary'}
                  onClick={() => setAnchorType('port')}
                >
                  An OLT PON port
                </Button>
                <Button
                  size="sm"
                  variant={anchorType === 'splitter' ? 'primary' : 'secondary'}
                  onClick={() => setAnchorType('splitter')}
                >
                  An existing splitter
                </Button>
              </div>
            </Field>

            {anchorType === 'port' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="OLT">
                  <select
                    className="input"
                    value={oltId}
                    onChange={(e) => {
                      setOltId(e.target.value);
                      setPonPort('');
                    }}
                  >
                    <option value="">Choose…</option>
                    {olts.map((olt) => (
                      <option key={olt.id} value={olt.id}>
                        {olt.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="PON port" hint="The port this cascade's light comes from">
                  {(ports.data?.items ?? []).length > 0 ? (
                    <select
                      className="input"
                      value={ponPort}
                      disabled={!oltId}
                      onChange={(e) => setPonPort(e.target.value)}
                    >
                      <option value="">Choose…</option>
                      {(ports.data?.items ?? []).map((p) => (
                        <option key={p.port} value={p.port}>
                          {p.port}
                          {p.onuCount != null ? ` — ${p.onuCount} ONUs` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={ponPort}
                      onChange={(e) => setPonPort(e.target.value)}
                      placeholder="1/2/1"
                      disabled={!oltId}
                    />
                  )}
                </Field>
              </div>
            ) : (
              <Field
                label="Splitter"
                hint="The chain grows out of its free outputs — sub-splitters and placed ONUs are counted"
              >
                <select
                  className="input"
                  value={anchorSplitterId}
                  onChange={(e) => {
                    setAnchorSplitterId(e.target.value);
                    const picked = splitters.find((s) => s.id === e.target.value);
                    if (picked?.zoneId) setZoneId(picked.zoneId);
                  }}
                >
                  <option value="">Choose…</option>
                  {[...splitters]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} — {SPLITTER_KIND_LABELS[s.kind]} 1×{s.capacity}
                      </option>
                    ))}
                </select>
              </Field>
            )}

            {anchorType === 'splitter' && anchor?.type === 'splitter' && (
              <p className="text-sm text-muted">{anchorLine}</p>
            )}

            <Field label="Zone" hint="Every splitter in the run lands in it">
              <ZoneSelect
                value={zoneId}
                zones={topology.zones}
                emptyLabel="No zone"
                onChange={setZoneId}
              />
            </Field>
          </>
        )}

        {step === 1 && (
          <>
            <p className="text-sm text-muted">
              Anchored on <span className="text-ink">{anchorLine}</span>. The dB figure is the
              typical insertion loss of the splitters down to each level, plus the fibre where a
              length is known — the routes measured down to the anchor, and any run you plan per
              level, at {FIBRE_DB_PER_KM} dB/km. Splices and connectors are on top.
            </p>

            <div className="space-y-3">
              {levels.map((level, i) => {
                const previousKind: SplitterKind | null =
                  i === 0
                    ? anchor?.type === 'splitter'
                      ? anchor.kind
                      : null
                    : (levels[i - 1]?.kind ?? null);
                const kindOptions = ALL_KINDS.filter(
                  (k) =>
                    k === level.kind ||
                    previousKind === null ||
                    SPLITTER_KIND_RANK[k] >= SPLITTER_KIND_RANK[previousKind],
                );
                const summary = plan.levels[i];
                const rootLocked = i === 0 && anchor?.type === 'port';
                return (
                  <div key={i} className="rounded-lg border border-line p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-xs uppercase tracking-wide text-faint">Level {i + 1}</p>
                      {summary && (
                        <span
                          className={`text-xs ${summary.amber ? 'text-warn' : 'text-faint'}`}
                          title="Typical insertion loss of the splitters down to and including this level, plus the fibre where a length is known"
                        >
                          {summary.count} splitter{summary.count === 1 ? '' : 's'} · −
                          {summary.lossDb.toFixed(1)} dB
                          {summary.fibreLossDb > 0 && ` (fibre +${summary.fibreLossDb.toFixed(1)})`}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <Field label="Sits in" hint="OLT port → ODF → enclosure → FAT">
                        <select
                          className="input w-36"
                          value={level.kind}
                          onChange={(e) => setLevel(i, { kind: e.target.value as SplitterKind })}
                        >
                          {kindOptions.map((k) => (
                            <option key={k} value={k}>
                              {SPLITTER_KIND_LABELS[k]}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Size">
                        <select
                          className="input w-24"
                          value={level.capacity}
                          onChange={(e) => setLevel(i, { capacity: Number(e.target.value) })}
                        >
                          {SPLITTER_CAPACITIES.map((size) => (
                            <option key={size} value={size}>
                              1×{size}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field
                        label={i === 0 ? 'On the anchor' : 'Per parent'}
                        hint={
                          rootLocked ? 'One main splitter per port' : 'Siblings hung on each feed'
                        }
                      >
                        <Input
                          type="number"
                          min={1}
                          max={32}
                          className="w-20"
                          value={rootLocked ? '1' : level.countPerParent}
                          disabled={rootLocked}
                          onChange={(e) => setLevel(i, { countPerParent: e.target.value })}
                        />
                      </Field>
                      <Field label="Name pattern" hint="{n} numbers them, e.g. FAT-{n}">
                        <Input
                          value={level.namePattern}
                          maxLength={120}
                          className="w-40"
                          onChange={(e) => setLevel(i, { namePattern: e.target.value })}
                        />
                      </Field>
                      <Field label="Fibre run (m)" hint="From the level above; blank adds nothing">
                        <Input
                          type="number"
                          min={0}
                          max={100000}
                          step={10}
                          className="w-24"
                          value={level.fibreLengthM}
                          placeholder="—"
                          onChange={(e) => setLevel(i, { fibreLengthM: e.target.value })}
                        />
                      </Field>
                      {i === levels.length - 1 && levels.length > 1 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setLevels((all) => all.slice(0, -1))}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="ghost"
                icon={<Plus className="size-3.5" />}
                disabled={levels.length >= maxLevels}
                title={
                  levels.length >= maxLevels
                    ? `Splitters cascade at most ${MAX_TOPOLOGY_DEPTH} sublevels below a main splitter`
                    : undefined
                }
                onClick={addLevel}
              >
                Add a level
              </Button>
              <span className="text-xs text-faint">
                {plan.total} splitter{plan.total === 1 ? '' : 's'} in this run · caps at{' '}
                {CASCADE_MAX_NODES}
              </span>
            </div>

            {plan.problems.length > 0 && (
              <div className="space-y-2">
                {plan.problems.map((problem) => (
                  <ErrorNotice key={problem} error={new Error(problem)} />
                ))}
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-sm text-muted">
              <span className="text-ink">
                {plan.total} splitter{plan.total === 1 ? '' : 's'}
              </span>{' '}
              will be created on <span className="text-ink">{anchorLine}</span>
              {zoneId
                ? `, in ${topology.zones.find((z) => z.id === zoneId)?.name ?? 'the chosen zone'}`
                : ', in no zone'}
              . A run caps at {CASCADE_MAX_NODES}.
            </p>

            {plan.problems.length > 0 && (
              <div className="space-y-2">
                {plan.problems.map((problem) => (
                  <ErrorNotice key={problem} error={new Error(problem)} />
                ))}
              </div>
            )}

            {plan.nodes.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-line">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-faint">
                      <th className="px-4 py-2 font-medium">Name</th>
                      <th className="px-4 py-2 font-medium">Stage</th>
                      <th className="px-4 py-2 font-medium">Hangs on</th>
                      <th className="px-4 py-2 text-right font-medium">Loss to here</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {plan.nodes.map((node) => (
                      <tr key={node.index}>
                        <td className="px-4 py-2 text-ink">{node.name}</td>
                        <td className="px-4 py-2 text-muted">
                          {SPLITTER_KIND_LABELS[node.kind]} · 1×{node.capacity}
                        </td>
                        <td className="px-4 py-2 text-muted">{node.parentLabel}</td>
                        <td
                          className={`px-4 py-2 text-right ${node.amber ? 'text-warn' : 'text-faint'}`}
                        >
                          −{node.lossDb.toFixed(1)} dB
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </>
    </WizardDialog>
  );
}
